import type { WordReference } from "@circleofa/acim-engine"
import { get, type Unsubscriber } from "svelte/store"
import {
    type HighlightID,
    type Highlight,
    highlights,
    highlighters,
    type Highlighter,
    updateHighlight,
    deleteHighlight,
} from "./highlights"

/**
 * `Remember to unsubscribe on dismount` !! This creates a temporary
 * store which reacts to changes in highlights. It's designed because
 * to subscribe to the whole store would mean we would have to iterate
 * through every single highlight in the whole book on the page. Now
 * we only have to iterate through the words filtered by the bounds of the page
 * and we listen to any changes in the highlights to see if those set of words
 * change.
 */
// export function subscribeHighlightSubtree(
//     start: ReferenceWeight,
//     end: ReferenceWeight,
//     highlighterID?: HighlighterID
// ) {
//     const { subscribe, update, set } = writable(
//         new Map() as Map<HighlightID, Highlight>
//     )
//     const unsubscribe = highlights.subscribe(() => {
//         const f: Map<HighlightID, Highlight> = new Map()
//         if (highlighterID) {
//             const requestedHighlighter = get(highlighters).get(highlighterID)
//             if (requestedHighlighter) {
//                 for (const [id, highlight] of requestedHighlighter.highlights) {
//                     // "In" style,
//                     if (highlight.start.referenceWeight >= start && highlight.start.referenceWeight <= end) {
//                         f.set(highlight.id, highlight)
//                     }

//                     // "Touching" style
//                     // if (
//                     //     (highlight.start.referenceWeight >= start &&
//                     //         highlight.start.referenceWeight <= end) ||
//                     //     (highlight.end.referenceWeight >= start &&
//                     //         highlight.end.referenceWeight <= end)
//                     // ) {
//                     //     f.set(highlight.id, highlight)
//                     // }
//                 }
//             } else {
//                 console.error("Highlighter not found!")
//             }
//         } else {
//             // We know all highlights will never exceed bounds for the main content
//             // which is the only place we can highlight, but we don't know for
//             // displaying highlight snippets, they may be cut off
//             for (const [_, highlighter] of get(highlighters)) {
//                 for (const [id, highlight] of highlighter.highlights) {
//                     if (
//                         (highlight.start.referenceWeight >= start &&
//                             highlight.start.referenceWeight <= end) ||
//                         (highlight.end.referenceWeight >= start &&
//                             highlight.end.referenceWeight <= end)
//                     ) {
//                         f.set(highlight.id, highlight)
//                     }
//                 }
//             }
//         }
//         set(f)
//     })

//     return {
//         subscribe,
//         unsubscribe,
//         changeStart(id: HighlightID, start: WordReference) {
//             const highlight = get({ subscribe }).get(id)
//             if (!highlight)
//                 throw new Error(
//                     `Highlight ${id} does not exist in highlight subtree!`
//                 )
//             highlight.start = start
//             update((r) => r)
//         },
//         changeEnd(id: HighlightID, end: WordReference) {
//             const highlight = get({ subscribe }).get(id)
//             if (!highlight)
//                 throw new Error(
//                     `Highlight ${id} does not exist in highlight subtree!`
//                 )
//             highlight.end = end
//             update((r) => r)
//         },
//         changeRange(id: HighlightID, start: WordReference, end: WordReference) {
//             const highlight = get({ subscribe }).get(id)
//             if (!highlight)
//                 throw new Error(
//                     `Highlight ${id} does not exist in highlight subtree!`
//                 )
//             highlight.start = start
//             highlight.end = end
//             update((r) => r)
//         },
//         changeHighlighter(id: HighlightID, highlighter: Highlighter) {
//             const highlight = get({ subscribe }).get(id)
//             if (!highlight)
//                 throw new Error(
//                     `Highlight ${id} does not exist in highlight subtree!`
//                 )
//             highlight.changeHighlighter(highlighter)
//             // Change highlighter will refresh highlighter store and reactively trickle down
//         },
//         commit(id: HighlightID) {
//             const highlight = get({ subscribe }).get(id)
//             if (!highlight)
//                 throw new Error(
//                     `Highlight ${id} does not exist in highlight subtree!`
//                 )
//             updateHighlight(highlight)
//         },
//     }
// }

/**
 *
 * **Important:** Make sure to call destroy() when this is over to unsubscribe from {@link highlights}
 */
export class HighlightSubtree {
    #map = new Map<HighlightID, Highlight>()
    #unsubFromHighlights: Unsubscriber

    constructor(
        public readonly start: WordReference,
        public readonly end: WordReference,
        public onChange: (highlights: IterableIterator<Highlight>) => void,
    ) {
        this.#unsubFromHighlights = highlights.subscribe(this.#onHighlightChange.bind(this))
    }

    /**
     * -------------------------------------------------------------------------
     *  📢 Highlight Change Emitter
     * -------------------------------------------------------------------------
     */

    getCurrentState() {
        return this.#map.values()
    }

    /**
     *
     */
    #onHighlightChange() {
        this.#map.clear()

        // Get all highlights within range supplied
        for (const [_, highlighter] of get(highlighters)) {
            for (const [id, highlight] of highlighter.highlights) {
                try {
                    const afterStart = highlight.start.referenceWeight >= this.start.referenceWeight
                    const beforeEnd = highlight.end.referenceWeight <= this.end.referenceWeight
                    if (afterStart && beforeEnd) {
                        this.#map.set(id, highlight)
                    }
                } catch (e) {
                    console.error(e)
                    console.warn("Attempting highlight deletion")
                    deleteHighlight(highlight.id)
                        .then(() => console.log("Rogue highlight deleted successfully"))
                        .catch(() => {
                            console.error(e)
                            throw new Error(
                                `Failed to process highlight in highlighter - needs manual deletion probably: ${highlighter.id}`,
                            )
                        })
                }
            }
        }

        // TODO: If a highlight is removed, we need to send a function to deselect it (e.g. realtime listener)
        return this.onChange(this.getCurrentState())
    }

    /**
     * -------------------------------------------------------------------------
     *  🪛 Highlight Handlers
     * -------------------------------------------------------------------------
     */

    /**
     * {@throws} if highlight not in map
     */
    changeRange(id: HighlightID, start: WordReference, end: WordReference) {
        const highlight = this.getHighlight(id)
        highlight.start = start
        highlight.end = end
        return this.onChange(this.getCurrentState())
    }

    /**
     * Change highlighter will refresh highlighter store and reactively trickle down.
     * We don't need to {@link commit} after using this
     * {@throws} if highlight not in map
     */
    changeHighlighter(id: HighlightID, highlighter: Highlighter) {
        const highlight = this.getHighlight(id)
        highlight.changeHighlighter(highlighter)
    }

    /**
     * Commit all changes to the store (this means write to database if
     * connected to firestore, or the local DB if not)
     * {@throws} if highlight not in map
     */
    commit(id: HighlightID) {
        const highlight = this.getHighlight(id)
        updateHighlight(highlight)
    }

    /**
     * -------------------------------------------------------------------------
     *  🔧 Utilities
     * -------------------------------------------------------------------------
     */

    /**
     * {@throws} if there's no highlighter in the store
     */
    getHighlight(id: HighlightID) {
        const highlight = this.#map.get(id)
        if (!highlight) throw new Error(`Highlight ${id} does not exist in highlight subtree!`)
        return highlight
    }

    /**
     * -------------------------------------------------------------------------
     *  🧹 Cleanup
     * -------------------------------------------------------------------------
     */

    destroy() {
        this.#unsubFromHighlights()
    }
}
